import React, { useState, useRef, useEffect, useContext } from 'react';
import { SdkContext } from '@low/containers/sdk-context';
import PropTypes from 'prop-types';

import BrandConfigProvider from '@low/containers/brand-config/brand-config-context';
import { setInterpolationDefaults, initI18n } from '@low/i18n';
import ffsConnector from '@low/dynamic/feature-flag/ffs-connector';
import FfsController from '@ringcentral/rwc-utils/src/feature-flag/controller/ffs/ffs-controller';
import { readLocallyStored } from '@ringcentral/rwc-utils/src/local-storage';
import { featureFlagClient } from '@ringcentral/rwc-utils/src/feature-flag-client';
import { FeatureFlagSourceName } from '@ringcentral/rwc-utils/src/feature-flag/types';

import { getPrefixedLogger, prefixes } from '@ringcentral/rwc-utils/src/rwc-logger';
import { PlaceholderLoader } from '@rwc/components/placeholder-loader';
import { AuthStatus } from '@low/types';
import { initAndGetFromJ } from '@low/index';
import { captureMessageLazily } from '@low/utils/lazy-sentry';

const logger = getPrefixedLogger(prefixes.VC_CONTAINERS, 'dynamic-manager');
export const WAIT_FOR_FFS_INITIALIZATION_KEY = 'waitForFfsInitialization';

const DynamicManager = ({ children }) => {
    const sdkContext = useContext(SdkContext);
    const sdk = sdkContext?.sdk();

    const [isBrandConfigReady, setIsBrandConfigReady] = useState(false);
    const [featureFlagInitialized, setFeatureFlagInitialized] = useState(false);
    const [localeInitialized, setLocaleInitialized] = useState(false);
    const [brandConfig, setBrandConfig] = useState(null);
    const ffsInitialCondition = useRef(null);

    const checkFeatureFlagInitialStatus = async () => {
        const startTs = Date.now();

        const isInitializedOnTime = await featureFlagClient.awaitInitializing();
        logger.info(
            isInitializedOnTime
                ? `FFS initialized in ${Date.now() - startTs}ms`
                : 'FFS not initialized within timeout'
        );

        if (!isInitializedOnTime) {
            const ffsInstance = FfsController.getInstance();
            if (ffsInstance) {
                captureMessageLazily(
                    () =>
                        `Initialize ffs timeout. conditions: ${JSON.stringify(
                            ffsInitialCondition.current
                        )},hasCalledInit:${!!ffsInstance._brandId},ffsServer:${
                            ffsInstance._ffsAuthServer
                        },ffsStatus:${ffsInstance._connectStatus},fetchedCount:${
                            ffsInstance._fetchedCount
                        },disposedCount:${ffsInstance._disposedCount},hasFlags:${!!Object.keys(
                            ffsInstance._ffsControllerFromSdk?.getAllFlags() || {}
                        ).length}`
                );
            }
        }

        setFeatureFlagInitialized(true);
    };

    const initLocale = async () => {
        const locale = readLocallyStored('locale');
        await initI18n(locale);
        setLocaleInitialized(true);
    };

    useEffect(() => {
        if (!brandConfig || !localeInitialized) {
            return;
        }

        const variables = {
            displayName: brandConfig.displayName,
            productName: brandConfig.productName,
        };
        const isUpdated = setInterpolationDefaults(variables);
        // Update page title and favicon
        if (isUpdated) {
            document.title = brandConfig.productName;
        }

        let iconElement = document.querySelector("link[rel*='icon']");
        if (iconElement) {
            iconElement.href = `${brandConfig?.assets?.faviconUrl}`;
        } else {
            iconElement = document.createElement('link');
            iconElement.rel = 'icon';
            iconElement.href = `${brandConfig?.assets?.faviconUrl}`;
            document.getElementsByTagName('head')[0].appendChild(iconElement);
        }

        setIsBrandConfigReady(true);
    }, [brandConfig, localeInitialized]);

    const isInitialized = isBrandConfigReady && featureFlagInitialized;

    useEffect(() => {
        if (isInitialized) {
            logger.info('app initialized');
        }
    }, [isInitialized]);

    useEffect(() => {
        checkFeatureFlagInitialStatus();
        initLocale();
        initAndGetFromJ();
    }, []);

    // init ffs
    useEffect(() => {
        const ffsSupported = featureFlagClient.isSourceSupported(FeatureFlagSourceName.FFS);
        const platform = sdk.platform();
        ffsInitialCondition.current = {
            authStatus: sdkContext?.authStatus,
            platform: !!sdk.platform(),
            ffsSupported: ffsSupported,
        };
        if (ffsSupported && platform && sdkContext?.authStatus === AuthStatus.SUCCESS) {
            ffsConnector.init(platform);
            return () => {
                ffsConnector.dispose();
            };
        }
    }, [sdk, sdkContext?.authStatus]);

    return (
        <BrandConfigProvider
            afterConfigLoaded={setBrandConfig}
            localeInitialized={localeInitialized}>
            {isInitialized ? children : <PlaceholderLoader />}
        </BrandConfigProvider>
    );
};

DynamicManager.propTypes = {
    children: PropTypes.node.isRequired,
};
export default DynamicManager;
